import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { apiClient, plants as plantsApi } from "@/lib/api";
import { exportPlantsPdf } from "@/lib/pdfExport";
import { useAuth } from "@/contexts/AuthContext";

/**
 * DownloadLibrarySection — single-select (CSV / PDF) + Download button
 * + format-specific hint. Replaced two separate list-row buttons with
 * a card that explains what each format is good for. Format choice +
 * "include TOC" flag persist to localStorage.
 */
export default function DownloadLibrarySection() {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Persisted format choice — returning users don't have to re-pick.
  const [downloadFormat, setDownloadFormat] = useState(() => {
    try { return localStorage.getItem("prairie.settings.downloadFormat") === "pdf" ? "pdf" : "csv"; }
    catch { return "csv"; }
  });
  useEffect(() => {
    try { localStorage.setItem("prairie.settings.downloadFormat", downloadFormat); } catch { /* private mode */ }
  }, [downloadFormat]);

  // Include TOC in PDF exports — booklet-length libraries benefit
  // from a Contents page. Only surfaced when PDF is selected.
  const [includeToc, setIncludeToc] = useState(() => {
    try { return localStorage.getItem("prairie.settings.pdfIncludeToc") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("prairie.settings.pdfIncludeToc", includeToc ? "1" : "0"); } catch { /* private mode */ }
  }, [includeToc]);

  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await apiClient.get("/plants/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `prairie-backup-${date}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings.exportSuccess"));
    } catch {
      toast.error(t("settings.exportFail"));
    } finally {
      setExporting(false);
    }
  };

  const downloadPdf = async () => {
    setExportingPdf(true);
    try {
      const { data } = await plantsApi.list();
      const items = data.items || [];
      if (items.length === 0) {
        toast.message(t("settings.exportPdfEmpty"));
        return;
      }
      await exportPlantsPdf(items, {
        language: user?.language || "en",
        state: user?.state || "",
        userName: user?.name,
        t,
        includeToc,
      });
      toast.success(t("settings.exportPdfSuccess"));
    } catch {
      toast.error(t("settings.exportPdfFail"));
    } finally {
      setExportingPdf(false);
    }
  };

  const busy = downloadFormat === "pdf" ? exportingPdf : exporting;

  return (
    <div
      data-testid="download-library-card"
      className="bg-white rounded-3xl border border-[#E8E5DF] p-5 space-y-3"
    >
      <div className="flex items-center gap-2 text-leaf-ink">
        <Download className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
        <h2 className="font-display text-lg leading-none" data-testid="download-library-title">
          {t("settings.downloadLibraryTitle")}
        </h2>
      </div>
      <div className="flex items-stretch gap-3">
        <Select
          value={downloadFormat}
          onValueChange={setDownloadFormat}
          disabled={exporting || exportingPdf}
        >
          <SelectTrigger
            className="flex-1 h-11 rounded-md bg-parchment/40 border-[#E8E5DF]"
            data-testid="download-format-select"
            aria-label={t("settings.downloadFormatAria")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="csv" data-testid="download-format-csv">CSV</SelectItem>
            <SelectItem value="pdf" data-testid="download-format-pdf">PDF</SelectItem>
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={downloadFormat === "pdf" ? downloadPdf : downloadCsv}
          disabled={busy}
          data-testid="download-library-button"
          className="h-11 px-5 rounded-md bg-leaf-deep hover:bg-leaf-ink disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium text-sm inline-flex items-center gap-2 transition shadow-botanical whitespace-nowrap"
        >
          {busy
            ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} aria-hidden="true" />
            : <Download className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />}
          <span>{t("settings.downloadCta")}</span>
        </button>
      </div>
      <div
        data-testid="download-format-hint"
        className="rounded-xl bg-parchment/50 border border-[#EFEBE3] px-4 py-3 text-sm text-leaf-mist leading-relaxed"
        aria-live="polite"
      >
        {t(`settings.downloadHint.${downloadFormat}`)}
      </div>
      {/* Table-of-contents toggle. PDF-only; hidden when CSV is
          selected so the card stays uncluttered. */}
      {downloadFormat === "pdf" && (
        <div
          data-testid="download-toc-row"
          className="flex items-start justify-between gap-3 pt-1"
        >
          <div className="flex-1 min-w-0">
            <label
              htmlFor="pdf-include-toc"
              className="block text-sm font-medium text-leaf-ink cursor-pointer"
            >
              {t("settings.includeTocLabel")}
            </label>
            <p className="text-xs text-leaf-mist mt-0.5 leading-snug">
              {t("settings.includeTocHint")}
            </p>
          </div>
          <Switch
            id="pdf-include-toc"
            data-testid="pdf-include-toc-switch"
            checked={includeToc}
            onCheckedChange={setIncludeToc}
            disabled={exportingPdf}
            aria-label={t("settings.includeTocLabel")}
          />
        </div>
      )}
    </div>
  );
}
